// ConfirmPH Corporate Accounting: the company's own books.
//
// Subscription, add-on and sponsored-placement money only. A rental
// business's own ledger never appears here; businesses show up only as the
// customer on an invoice.
import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { AlertTriangle, BadgeCheck, Building2, CheckCircle2, FileSpreadsheet, PlayCircle, ShieldAlert } from "lucide-react";

import { api, formatApiError } from "../../lib/api";
import { RECORDS_NOTE, docTypeLabel, downloadFile, fmtMinor, idemHeaders, monthStartISO, todayISO }
  from "../../lib/books";
import { Button, Empty, Field, Loading, Modal, Money, Pill, Section, Stat, Table, inputClass }
  from "../../components/finance/shared";

const CORP = "/admin/corporate-finance";
const TABS = [
  { key: "overview", label: "Overview" },
  { key: "approvals", label: "Approvals" },
  { key: "revenue", label: "Revenue" },
  { key: "reconciliation", label: "Billing reconciliation" },
  { key: "reports", label: "Reports" },
  { key: "roles", label: "Access" },
];

export default function CorporateFinance() {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("overview");
  const [refresh, setRefresh] = useState(0);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`${CORP}/status`);
      setStatus(data);
    } catch (err) {
      setStatus(null);
      if (err?.response?.status !== 403) toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load, refresh]);

  if (loading) return <Loading label="Opening the corporate ledger..." />;
  if (!status) {
    return (
      <Section title="Corporate accounting">
        <Empty>You don't have access to ConfirmPH's own accounting records.</Empty>
      </Section>
    );
  }

  const active = status.profile?.status === "active";

  return (
    <div className="space-y-4" data-testid="corporate-finance-page">
      <div>
        <div className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">
          Internal Accounting Records
        </div>
        <h1 className="font-display text-2xl font-bold mt-1">Corporate accounting</h1>
        <p className="text-sm text-slate-600 mt-1 max-w-3xl">{RECORDS_NOTE}</p>
      </div>

      {!active ? <Activate status={status} onDone={() => setRefresh((n) => n + 1)} /> : (
        <>
          <div className="overflow-x-auto">
            <div className="flex gap-1.5 min-w-max">
              {TABS.map((t) => (
                <button key={t.key} onClick={() => setTab(t.key)} data-testid={`corp-tab-${t.key}`}
                        className={`rounded-lg px-3 py-2 text-sm font-medium border ${tab === t.key
                          ? "border-slate-900 bg-slate-900 text-white"
                          : "border-slate-200 bg-white text-slate-700 hover:bg-slate-50"}`}>
                  {t.label}
                </button>
              ))}
            </div>
          </div>
          <div key={`${tab}-${refresh}`}>
            {tab === "overview" && <Overview status={status} />}
            {tab === "approvals" && <Approvals onChanged={() => setRefresh((n) => n + 1)} />}
            {tab === "revenue" && <Revenue />}
            {tab === "reconciliation" && <Reconciliation />}
            {tab === "reports" && <Reports />}
            {tab === "roles" && <Roles />}
          </div>
        </>
      )}
    </div>
  );
}

function Activate({ status, onDone }) {
  const [form, setForm] = useState({ entity_name: "ConfirmPH", go_live_date: todayISO(),
                                     opening_balance_date: todayISO(), fiscal_year_start_month: 1 });
  const [busy, setBusy] = useState(false);
  const supported = status.transactions?.supported;

  const activate = async () => {
    setBusy(true);
    try {
      await api.post(`${CORP}/activate`, { confirm: "ACTIVATE", ...form });
      toast.success("Corporate accounting is live.");
      onDone();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Section title="Start the corporate ledger"
             description="Until this is on, billing keeps its verified payment records and nothing is posted."
             testId="corp-activate">
      <div className={`rounded-xl border p-3 text-sm ${supported ? "border-emerald-200 bg-emerald-50/60"
                                                                  : "border-rose-200 bg-rose-50/60"}`}>
        {supported ? <CheckCircle2 className="w-4 h-4 inline mr-1 text-emerald-600" />
                   : <AlertTriangle className="w-4 h-4 inline mr-1 text-rose-600" />}
        Database transactions: <span className="font-medium">{status.transactions?.topology}</span>
        {supported ? " - supported." : " - not available. Accounting needs a replica set or sharded cluster."}
      </div>
      <div className="mt-3 rounded-xl border border-amber-200 bg-amber-50/60 p-3 text-sm">
        <ShieldAlert className="w-4 h-4 inline mr-1 text-amber-600" />
        Stripe stays unposted until it is production-verified. The fiscal year is assumed to be the calendar year
        until it is confirmed from the registration documents.
      </div>
      <div className="mt-3 grid sm:grid-cols-2 gap-3">
        <Field label="Registered entity name">
          <input className={inputClass} value={form.entity_name} data-testid="corp-entity-name"
                 onChange={(e) => setForm({ ...form, entity_name: e.target.value })} />
        </Field>
        <Field label="Fiscal year starts (month)">
          <input type="number" min={1} max={12} className={inputClass} value={form.fiscal_year_start_month}
                 onChange={(e) => setForm({ ...form, fiscal_year_start_month: Number(e.target.value) })} />
        </Field>
        <Field label="Ledger start date">
          <input type="date" className={inputClass} value={form.go_live_date}
                 onChange={(e) => setForm({ ...form, go_live_date: e.target.value })} />
        </Field>
        <Field label="Opening balance date">
          <input type="date" className={inputClass} value={form.opening_balance_date}
                 onChange={(e) => setForm({ ...form, opening_balance_date: e.target.value })} />
        </Field>
      </div>
      <Button className="mt-3" onClick={activate} busy={busy} disabled={!supported} data-testid="corp-activate-btn">
        <BadgeCheck className="w-4 h-4" /> Activate corporate accounting
      </Button>
    </Section>
  );
}

function Overview({ status }) {
  const [tb, setTb] = useState(null);
  const [receivables, setReceivables] = useState(null);
  const [exceptions, setExceptions] = useState([]);

  useEffect(() => {
    Promise.all([api.get(`${CORP}/reports/trial-balance`), api.get(`${CORP}/reports/receivables`),
                 api.get(`${CORP}/exceptions`)])
      .then(([a, b, c]) => { setTb(a.data); setReceivables(b.data); setExceptions(c.data || []); })
      .catch((err) => toast.error(formatApiError(err)));
  }, []);

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <Stat label="Accounts" value={(tb?.rows || []).length} />
        <Stat label="Trial balance" value={tb?.balanced ? "Balanced" : "Check"} testId="corp-tb-state"
              tone={tb?.balanced ? "text-emerald-700" : "text-rose-600"} />
        <Stat label="Owed by customers" value={fmtMinor(receivables?.total_open_minor)} />
        <Stat label="Open exceptions" value={exceptions.length}
              tone={exceptions.length ? "text-rose-600" : "text-slate-900"} />
      </div>
      <Section title="Commission and reward programs"
               description="Designed and ready, deliberately not posting anything." testId="corp-commissions">
        <div className="space-y-2 text-sm">
          {Object.entries(status.commissions || {}).map(([program, info]) => (
            <div key={program} className="rounded-xl border border-slate-200 p-3">
              <div className="font-medium">{program.replace(/_/g, " ")}</div>
              <div className="text-slate-600">Status: {info.status.replace(/_/g, " ")} · {info.event_source?.replace(/_/g, " ")}</div>
              <div className="text-xs text-slate-500 mt-1">Missing: {(info.missing || []).join("; ")}</div>
            </div>
          ))}
        </div>
      </Section>
      <Section title="Open exceptions" testId="corp-exceptions">
        {exceptions.length === 0 ? <Empty>Nothing to review.</Empty> : (
          <div className="space-y-2">
            {exceptions.map((row) => (
              <div key={row.id} className="rounded-xl border border-slate-200 p-3">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="font-medium">{row.title}</span><Pill status={row.severity}>{row.severity}</Pill>
                </div>
                <p className="text-sm text-slate-600">{row.message}</p>
              </div>
            ))}
          </div>
        )}
      </Section>
    </div>
  );
}

function Approvals({ onChanged }) {
  const [rows, setRows] = useState([]);
  const [creating, setCreating] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`${CORP}/approvals`, { params: { status: "pending_approval" } });
      setRows(data || []);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const decide = async (row, approve) => {
    const note = window.prompt(approve ? "Note for the approval (optional)" : "Why is this rejected?") || "";
    if (!approve && note.trim().length < 5) return;
    try {
      await api.post(`${CORP}/approvals/${row.id}/${approve ? "approve" : "reject"}`, { note: note.trim() || null });
      toast.success(approve ? "Approved and posted." : "Rejected.");
      load();
      onChanged?.();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  if (loading) return <Loading />;

  return (
    <div className="space-y-4">
      <Section title="Waiting for a second person"
               description="Manual payments, refunds, write-offs and period locks need two different people."
               actions={<Button onClick={() => setCreating(true)} data-testid="corp-new-manual-payment">
                          Record a manual payment
                        </Button>}
               testId="corp-approvals">
        <Table head={["Request", "Asked by", { key: "a", label: "Amount", align: "right" }, ""]}
               empty={rows.length === 0 && <Empty>Nothing is waiting.</Empty>}>
          {rows.map((row) => (
            <tr key={row.id} data-testid={`corp-approval-${row.id}`}>
              <td className="py-2 pr-3">
                <div className="font-medium">{row.action.replace(/_/g, " ")}</div>
                <div className="text-xs text-slate-500">{row.reason}</div>
              </td>
              <td className="py-2 pr-3 text-slate-600">{row.created_by_email || row.requested_by_role}</td>
              <td className="py-2 pr-3 text-right"><Money minor={row.total_minor} /></td>
              <td className="py-2 pr-3">
                <div className="flex justify-end gap-1">
                  <Button variant="secondary" onClick={() => decide(row, true)}
                          data-testid={`corp-approve-${row.id}`}>Approve</Button>
                  <Button variant="danger" onClick={() => decide(row, false)}>Reject</Button>
                </div>
              </td>
            </tr>
          ))}
        </Table>
      </Section>
      {creating && <ManualPaymentModal onClose={() => setCreating(false)}
                                       onDone={() => { setCreating(false); load(); }} />}
    </div>
  );
}

function ManualPaymentModal({ onClose, onDone }) {
  const [accounts, setAccounts] = useState([]);
  const [form, setForm] = useState({ company_id: "", kind: "plan", plan: "pro", period_days: 30, amount: "",
                                     payment_account_id: "", payment_reference: "", evidence_note: "",
                                     addon_key: "monthly_fleet_points", reason: "", payment_date: todayISO() });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get(`${CORP}/accounts`).then(({ data }) =>
      setAccounts((data || []).filter((a) => a.is_payment_account && a.active))).catch(() => {});
  }, []);

  const submit = async () => {
    setBusy(true);
    try {
      await api.post(`${CORP}/approvals`, { action: "manual_payment", payload: { ...form } });
      toast.success("Recorded. A second person must approve it before anything posts.");
      onDone();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title="Manual payment received" onClose={onClose} testId="corp-manual-payment-modal"
           description="Money received outside online checkout. Nothing posts and no entitlement changes until a second person approves.">
      <div className="grid sm:grid-cols-2 gap-3">
        <Field label="Business (company id)" className="sm:col-span-2">
          <input className={inputClass} value={form.company_id} data-testid="corp-mp-company"
                 onChange={(e) => setForm({ ...form, company_id: e.target.value })} />
        </Field>
        <Field label="What was paid for">
          <select className={inputClass} value={form.kind} data-testid="corp-mp-kind"
                  onChange={(e) => setForm({ ...form, kind: e.target.value })}>
            <option value="plan">Subscription plan</option>
            <option value="addon">Add-on</option>
            <option value="sponsored_placement">Sponsored placement</option>
            <option value="existing_invoice">An existing invoice</option>
          </select>
        </Field>
        {form.kind === "plan" && (
          <Field label="Plan">
            <select className={inputClass} value={form.plan}
                    onChange={(e) => setForm({ ...form, plan: e.target.value })}>
              {["basic", "pro", "enterprise", "custom"].map((p) => <option key={p} value={p}>{p}</option>)}
            </select>
          </Field>
        )}
        <Field label="Amount (₱)">
          <input className={inputClass} inputMode="decimal" value={form.amount} data-testid="corp-mp-amount"
                 onChange={(e) => setForm({ ...form, amount: e.target.value })} />
        </Field>
        <Field label="Date received">
          <input type="date" className={inputClass} value={form.payment_date}
                 onChange={(e) => setForm({ ...form, payment_date: e.target.value })} />
        </Field>
        <Field label="Into which account">
          <select className={inputClass} value={form.payment_account_id} data-testid="corp-mp-account"
                  onChange={(e) => setForm({ ...form, payment_account_id: e.target.value })}>
            <option value="">Select</option>
            {accounts.map((a) => <option key={a.id} value={a.id}>{a.code} {a.name}</option>)}
          </select>
        </Field>
        <Field label="Bank / e-wallet reference">
          <input className={inputClass} value={form.payment_reference}
                 onChange={(e) => setForm({ ...form, payment_reference: e.target.value })} />
        </Field>
        <Field label="Where is the evidence kept?" className="sm:col-span-2">
          <input className={inputClass} value={form.evidence_note} data-testid="corp-mp-evidence"
                 onChange={(e) => setForm({ ...form, evidence_note: e.target.value })} />
        </Field>
        <Field label="Reason" className="sm:col-span-2">
          <input className={inputClass} value={form.reason}
                 onChange={(e) => setForm({ ...form, reason: e.target.value })} />
        </Field>
      </div>
      <Button onClick={submit} busy={busy} data-testid="corp-mp-save">Send for approval</Button>
    </Modal>
  );
}

function Revenue() {
  const [schedules, setSchedules] = useState([]);
  const [period, setPeriod] = useState(() => {
    const d = new Date();
    d.setMonth(d.getMonth() - 1);
    return d.toISOString().slice(0, 7);
  });
  const [busy, setBusy] = useState(false);

  const load = () => api.get(`${CORP}/revenue/schedules`, { params: { status: "active" } })
    .then(({ data }) => setSchedules(data || [])).catch((err) => toast.error(formatApiError(err)));
  useEffect(() => { load(); }, []);

  const recognize = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`${CORP}/revenue/recognize`, { through_period: period });
      toast.success(`Recognized ${fmtMinor(data.recognized_minor)} across ${data.journals_posted} entries.`);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Section title="Deferred revenue"
             description="Subscriptions, monthly add-ons and placements are earned over the service period."
             testId="corp-revenue"
             actions={
               <div className="flex flex-wrap gap-2">
                 <input type="month" className={inputClass} value={period} data-testid="corp-recognize-period"
                        onChange={(e) => setPeriod(e.target.value)} />
                 <Button onClick={recognize} busy={busy} data-testid="corp-recognize-run">
                   <PlayCircle className="w-4 h-4" /> Recognize through {period}
                 </Button>
               </div>}>
      <Table head={["Product", "Customer", "Service period", { key: "b", label: "Unearned", align: "right" }]}
             empty={schedules.length === 0 && <Empty>Nothing is deferred right now.</Empty>}>
        {schedules.map((s) => (
          <tr key={s.id}>
            <td className="py-2 pr-3">{s.product_code}</td>
            <td className="py-2 pr-3 text-slate-600">{s.customer_company_name || s.customer_company_id}</td>
            <td className="py-2 pr-3 text-slate-600">
              {String(s.segment?.start || "").slice(0, 10)} - {String(s.segment?.end || "").slice(0, 10)}
            </td>
            <td className="py-2 pr-3 text-right"><Money minor={s.deferred_balance_minor} /></td>
          </tr>
        ))}
      </Table>
    </Section>
  );
}

function Reconciliation() {
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get(`${CORP}/billing-reconciliation`).then(({ data: body }) => setData(body))
      .catch((err) => toast.error(formatApiError(err)));
  }, []);
  if (!data) return <Loading />;
  return (
    <div className="space-y-4">
      <Section title="Verified payments not in the books" testId="corp-unposted">
        <Table head={["Payment", "Business", { key: "a", label: "Amount", align: "right" }]}
               empty={data.verified_payments_not_in_books.length === 0 && <Empty>All posted.</Empty>}>
          {data.verified_payments_not_in_books.map((row) => (
            <tr key={row.id}>
              <td className="py-2 pr-3">{row.provider} · {row.provider_payment_id || row.intent_id}</td>
              <td className="py-2 pr-3 text-slate-600">{row.company_id}</td>
              <td className="py-2 pr-3 text-right"><Money minor={row.amount_minor} /></td>
            </tr>
          ))}
        </Table>
      </Section>
      <Section title="Manual activations without an amount"
               description={data.note} testId="corp-manual-worklist">
        <Table head={["When", "Business", "What happened"]}
               empty={data.manual_activations_without_amounts.length === 0 && <Empty>Nothing to reconcile.</Empty>}>
          {data.manual_activations_without_amounts.slice(0, 50).map((row, index) => (
            <tr key={`${row.created_at}-${index}`}>
              <td className="py-2 pr-3 text-slate-600">{String(row.created_at).slice(0, 10)}</td>
              <td className="py-2 pr-3 text-slate-600"><Building2 className="w-3.5 h-3.5 inline mr-1" />{row.company_id}</td>
              <td className="py-2 pr-3">{row.description}</td>
            </tr>
          ))}
        </Table>
      </Section>
    </div>
  );
}

function Reports() {
  const [from, setFrom] = useState(monthStartISO());
  const [to, setTo] = useState(todayISO());
  const [tb, setTb] = useState(null);

  useEffect(() => {
    api.get(`${CORP}/reports/trial-balance`, { params: { as_of: to } })
      .then(({ data }) => setTb(data)).catch((err) => toast.error(formatApiError(err)));
  }, [to]);

  const download = async (kind) => {
    try {
      const path = kind === "package"
        ? `${CORP}/exports/accountant-package.zip?date_from=${from}&date_to=${to}`
        : `${CORP}/exports/${kind}.csv?date_from=${from}&date_to=${to}`;
      await downloadFile(path, kind === "package" ? `corporate-records-${from}-${to}.zip`
                                                  : `corporate-${kind}-${from}-${to}.csv`);
    } catch (err) {
      toast.error(err.message);
    }
  };

  return (
    <Section title="Corporate reports" testId="corp-reports"
             actions={
               <div className="flex flex-wrap gap-2">
                 <input type="date" className={inputClass} value={from} onChange={(e) => setFrom(e.target.value)} />
                 <input type="date" className={inputClass} value={to} onChange={(e) => setTo(e.target.value)} />
                 <Button variant="secondary" onClick={() => download("journal-lines")}>
                   <FileSpreadsheet className="w-4 h-4" /> Journal lines
                 </Button>
                 <Button onClick={() => download("package")} data-testid="corp-export-package">
                   Accountant package
                 </Button>
               </div>}>
      <Table head={["Code", "Account", { key: "d", label: "Debit", align: "right" },
                    { key: "c", label: "Credit", align: "right" }]}
             empty={!tb && <Loading />}>
        {(tb?.rows || []).map((row) => (
          <tr key={row.account_id}>
            <td className="py-2 pr-3 text-slate-500">{row.code}</td>
            <td className="py-2 pr-3">{row.name}</td>
            <td className="py-2 pr-3 text-right">
              {row.balance_debit_minor ? <Money minor={row.balance_debit_minor} /> : ""}</td>
            <td className="py-2 pr-3 text-right">
              {row.balance_credit_minor ? <Money minor={row.balance_credit_minor} /> : ""}</td>
          </tr>
        ))}
      </Table>
    </Section>
  );
}

function Roles() {
  const [roles, setRoles] = useState([]);
  const [form, setForm] = useState({ user_id: "", role: "accountant", reason: "" });
  const [busy, setBusy] = useState(false);

  const load = () => api.get(`${CORP}/roles`).then(({ data }) => setRoles(data || []))
    .catch((err) => toast.error(formatApiError(err)));
  useEffect(() => { load(); }, []);

  const grant = async () => {
    setBusy(true);
    try {
      await api.post(`${CORP}/roles`, form);
      toast.success("Role granted.");
      setForm({ user_id: "", role: "accountant", reason: "" });
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Section title="Who can work in the corporate books"
             description="Business accounts can never hold these roles." testId="corp-roles">
      <Table head={["User", "Role", "Status"]} empty={roles.length === 0 && <Empty>No roles granted yet.</Empty>}>
        {roles.map((row) => (
          <tr key={row.id}>
            <td className="py-2 pr-3">{row.user_email || row.user_id}</td>
            <td className="py-2 pr-3">{row.role.replace(/_/g, " ")}</td>
            <td className="py-2 pr-3"><Pill status={row.status === "active" ? "posted" : "reversed"}>{row.status}</Pill></td>
          </tr>
        ))}
      </Table>
      <div className="mt-3 grid sm:grid-cols-3 gap-3">
        <Field label="User id">
          <input className={inputClass} value={form.user_id} data-testid="corp-role-user"
                 onChange={(e) => setForm({ ...form, user_id: e.target.value })} />
        </Field>
        <Field label="Role">
          <select className={inputClass} value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })}>
            <option value="finance_admin">Finance admin (records money)</option>
            <option value="accountant">Accountant (closes and corrects)</option>
            <option value="finance_viewer">Viewer (read only)</option>
          </select>
        </Field>
        <Field label="Reason">
          <input className={inputClass} value={form.reason}
                 onChange={(e) => setForm({ ...form, reason: e.target.value })} />
        </Field>
      </div>
      <Button className="mt-3" onClick={grant} busy={busy} data-testid="corp-role-grant">Grant role</Button>
    </Section>
  );
}
